/**
 * Keynote Tool — Annotate > Tag > Keynote split button.
 *
 * Variants:
 *   element  — reads the element's "Keynote" parameter
 *   material — reads the keynote of the clicked face's material layer
 *   user     — prompts for a free CSI MasterFormat-style code (via the text
 *              note input event shared with the Text tool)
 *
 * Placement: click the target element; the numbered bubble drops with a
 * leader pointing at it.
 */

import { v4 as uuidv4 } from 'uuid';
import { Element } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';
import i18n from '../i18n';

export type KeynoteKind = 'element' | 'material' | 'user';

export interface KeynoteGeometryData {
    position: [number, number, number];
    leader: boolean;
    targetId?: string;
    leaderTip?: [number, number, number];
}

export interface KeynoteElement extends Element {
    category: 'Detail Items'; // Keynotes are often detail items or tags
    geometryData: KeynoteGeometryData;
}

const KIND_LABEL: Record<KeynoteKind, string> = {
    element: 'Element Keynote',
    material: 'Material Keynote',
    user: 'User Keynote',
};

export class KeynoteTool extends BaseTool {
    id = 'keynote';
    name: string;
    cursor = 'crosshair';

    private kind: KeynoteKind;
    private awaitingText = false;
    private pendingPosition: THREE.Vector3 | null = null;
    private pendingTarget: string | null = null;
    private textInputHandler: ((e: Event) => void) | null = null;
    private nextUserKeynote = 1;

    constructor(kind: KeynoteKind = 'element') {
        super();
        this.kind = kind;
        this.id = `keynote_${kind}`;
        this.name = KIND_LABEL[kind];
    }

    protected onActivate(): void {
        if (!this.textInputHandler) {
            this.textInputHandler = ((e: Event) => {
                if (!this.context || !this.awaitingText) return;
                this.awaitingText = false;
                const detail = (e as CustomEvent).detail as { text: string | null } | undefined;
                const pos = this.pendingPosition;
                this.pendingPosition = null;
                this.context.appStore.setToolPreview(null);
                if (detail?.text && pos) {
                    this.createKeynote(pos, this.pendingTarget, detail.text);
                }
                this.pendingTarget = null;
            }) as EventListener;
            window.addEventListener('yap:text-note-input', this.textInputHandler);
        }
        this.notify(i18n.t('tools.notify.keynoteClick', { kind: KIND_LABEL[this.kind] }));
    }

    deactivate(): void {
        this.awaitingText = false;
        this.pendingPosition = null;
        this.pendingTarget = null;
        super.deactivate();
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;
        if (this.awaitingText) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point, 0.1);
        const targetId = this.findElementIdAtPoint(event);

        if (this.kind === 'user') {
            // User keynotes need a typed code/number: reuse the text entry.
            this.pendingPosition = snapped;
            this.pendingTarget = targetId;
            this.awaitingText = true;
            this.context.appStore.setToolPreview({
                type: 'keynote',
                kind: this.kind,
                position: snapped,
                textEntering: true,
            });
            return;
        }

        let value: string | undefined;
        if (targetId) {
            const target = this.context.bimStore.project.elements.get(targetId);
            if (target) {
                value = this.kind === 'element'
                    ? target.parameters['Keynote'] ?? undefined
                    : target.parameters['Material Keynote']
                        ?? target.parameters['Keynote']
                        ?? undefined;
            }
        }
        if (!value) {
            value = String(this.nextUserKeynote++);
            this.notify(i18n.t('tools.notify.keynoteFallback', { kind: KIND_LABEL[this.kind], value: value }),
                'warning',
            );
        }
        this.createKeynote(snapped, targetId, value);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (this.awaitingText) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point, 0.1);
            const targetId = this.findElementIdAtPoint(event);
            this.context.appStore.setToolPreview({
                type: 'keynote',
                kind: this.kind,
                position: snapped,
                targetId,
            });
        }
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (this.awaitingText && event.key === 'Escape') {
            this.awaitingText = false;
            this.pendingPosition = null;
            this.pendingTarget = null;
            this.context?.appStore.setToolPreview(null);
            return;
        }
        super.onKeyDown(event);
    }

    private createKeynote(point: THREE.Vector3, targetId: string | null, value: string) {
        if (!this.context) return;

        const geometryData: KeynoteGeometryData = {
            position: [point.x, point.y, point.z],
            leader: true,
            targetId: targetId ?? undefined,
        };
        if (targetId) {
            const target = this.context.bimStore.project.elements.get(targetId);
            const tg = (target as any)?.geometryData;
            const tp = tg?.position;
            if (Array.isArray(tp)) {
                geometryData.leaderTip = [tp[0], tp[1], (tp[2] ?? 0) + 0.2];
            } else if (Array.isArray(tg?.start) && Array.isArray(tg?.end)) {
                // Linear hosts (walls, ducts, pipes, detail lines): leader
                // tip at the stroke midpoint instead of losing the leader.
                geometryData.leaderTip = [
                    (tg.start[0] + tg.end[0]) / 2,
                    (tg.start[1] + tg.end[1]) / 2,
                    (tg.start[2] ?? 0) + 0.2,
                ];
            } else if (Array.isArray(tg?.boundary) && tg.boundary.length >= 3) {
                // Sketch hosts (regions, clouds): tip at the boundary centroid.
                let cx = 0, cy = 0;
                tg.boundary.forEach(([bx, by]: [number, number]) => { cx += bx; cy += by; });
                geometryData.leaderTip = [cx / tg.boundary.length, cy / tg.boundary.length, 0.2];
            }
        }

        const keynote: KeynoteElement = {
            id: uuidv4(),
            category: 'Detail Items',
            type: KIND_LABEL[this.kind],
            parameters: {
                'Key Value': value,
                'Keynote Type': KIND_LABEL[this.kind],
                'Keynote Text': `Keynote ${value}`,
            },
            constraints: [],
            geometryData
        };

        this.context.bimStore.addElement(keynote);
    }
}
